<template>
  <div class="is-default-theme">
    <div
      v-if="hasIntroAnimationStarted"
      class="clara-bubble-glow is-circular has-pointer-events-none"
    />

    <div class="clara-for-planners-bubble b-24 r-24">
      <ClaraForPlannersLauncher v-show="!isOpen" :is-animating="isIntroAnimating" @open="onOpen" />
      <!-- The panel root uses Bulma's is-flex (display: flex !important), which overrides v-show. -->
      <div v-if="hasBeenOpened" v-show="isOpen">
        <ClaraForPlannersPanel :conversation="conversation" @minimize="onMinimize" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ClaraForPlannersLauncher from '@/modules/ai/components/presentational/clara-for-planners/ClaraForPlannersLauncher.vue';
import ClaraForPlannersPanel from '@/modules/ai/components/stateful/clara-for-planners/ClaraForPlannersPanel.vue';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import ClaraService from '@/modules/ai/services/ClaraService';
import { commits, getters } from '@/modules/core/app/helpers/store';
import { defineComponent } from 'vue';

const INTRO_ANIMATION_DURATION_MS: number = 8000;

export default defineComponent({
  name: 'ClaraForPlannersBubble',
  components: {
    ClaraForPlannersLauncher,
    ClaraForPlannersPanel
  },
  data(): {
    isOpen: boolean;
    hasBeenOpened: boolean;
    hasIntroAnimationStarted: boolean;
    isIntroAnimating: boolean;
    introAnimationTimer: ReturnType<typeof setTimeout> | null;
    conversation: ClaraConversationObject;
  } {
    return {
      isOpen: false,
      hasBeenOpened: false,
      hasIntroAnimationStarted: false,
      isIntroAnimating: false,
      introAnimationTimer: null,
      conversation: ClaraConversationObject.factory({ messages: [] })
    };
  },
  computed: {
    hasViewedLauncher(): boolean {
      return !!getters.auth.getUser()?.hasViewedClaraForPlannersLauncher;
    }
  },
  methods: {
    onOpen(): void {
      this.isOpen = true;
      this.hasBeenOpened = true;
    },
    onMinimize(): void {
      this.isOpen = false;
    },
    playIntroAnimation(): void {
      this.hasIntroAnimationStarted = true;
      this.isIntroAnimating = true;
      // Keeps a remount in this session from replaying the intro.
      commits.auth.setHasViewedClaraForPlannersLauncher();
      // A failed write only replays the intro on the next login.
      ClaraService.setHasViewedClaraForPlannersLauncher().catch(() => {});

      this.introAnimationTimer = setTimeout(() => {
        this.isIntroAnimating = false;
      }, INTRO_ANIMATION_DURATION_MS);
    }
  },
  mounted(): void {
    if (this.hasViewedLauncher) {
      return;
    }

    this.playIntroAnimation();
  },
  beforeUnmount(): void {
    if (this.introAnimationTimer) {
      clearTimeout(this.introAnimationTimer);
    }
  }
});
</script>

<style scoped>
.clara-for-planners-bubble {
  position: fixed;
  z-index: var(--ce-z-index-clara-bubble);
}

.clara-bubble-glow {
  position: fixed;
  z-index: var(--ce-z-index-clara-glow);
  right: -260px;
  bottom: -200px;
  width: 795px;
  height: 794px;
  background: linear-gradient(322deg, var(--ce-primary) 16.94%, rgba(255, 134, 91, 0.1) 87%);
  filter: blur(60px);
  opacity: 0;
  transform-origin: 78% 78%;
  animation: clara-glow-collapse 8s linear forwards;
}

@keyframes clara-glow-collapse {
  0% {
    opacity: 0;
    transform: scale(1);
  }
  3.75% {
    opacity: 0;
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  }
  12.5% {
    opacity: 0.3;
    transform: scale(1);
  }
  40% {
    opacity: 0.25;
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  }
  72.5% {
    opacity: 0;
    transform: scale(0.06);
  }
  100% {
    opacity: 0;
    transform: scale(0.06);
  }
}
</style>
